import { Component, ChangeDetectorRef } from '@angular/core';
import * as _ from 'lodash';

import { descriptionSegmentData } from '../../../segment-data/description-segment-data';

import { BaseScorecardComponent } from '../base/_base-scorecard.component';
import { SegmentFieldItem, ValidationItem } from 'utils/segment-field-item.interface';
import { ScorecardItemComponent } from 'shared/scorecard/scorecard-item/scorecard-item.component';

import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';


@Component({
  moduleId: module.id,
  selector: 'description-scorecard',
  templateUrl: 'description-scorecard.component.html',
  styleUrls: ['description-scorecard.component.scss'],
})
export class DescriptionScorecardComponent extends BaseScorecardComponent {
  segmentTitle: string = 'Descriptions';
  segmentData: any = descriptionSegmentData;

  languagesList: Array<any> = [
    { code: 'EN', title: 'English' },
    { code: 'ES', title: 'Spanish' },
    { code: 'FR', title: 'French' },
  ];

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get segmentScores(): any {
    return _.get(this.scoreData, 'segments.descriptions.tallies');
  }

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
  ) {
    super(ref, scorecardConnectorService);
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onLangDetailsExpand(item: any, language: any) {
    this.scorecardConnectorService.scoreDetailsExpanded.emit({
      item, language: language.code
    });
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  getItemData(item: SegmentFieldItem, language: string): Array<string> {
    return _.get(this.itemDetail, `${item.item_name}.${language}`);
  }

  getListItems(item: SegmentFieldItem, language: string): Array<string> {
    return this.getItemData(item, language) || [''];
  }

  /*
    * Checks every language score validity
   */
  isDescriptionItemValid(item: SegmentFieldItem): boolean {
    // get real type
    const type: string = this.getRealType(item);

    if (type === 'list') {
      return _.every(this.languagesList, (language: any) => {
        return _.every(this.getListItems(item, language.code), (value: string, index: number) => {
          const score = this.getScoreIndexItem(item, language.code, index);
          return _.get(score, 'valid', 1) === 1;
        });
      });
    } else {
      return _.every(this.languagesList, (language: any) => {
        const score = this.getScoreItem(item, language.code);
        return _.get(score, 'valid', 1) === 1;
      });
    }
  }

  /*
    * Checks every score in the list of the language
   */
  isScoreListValid(item: SegmentFieldItem, language: string) {
    return _.every(this.getListItems(item, language), (value: string, index: number) => {
      const score = this.getScoreIndexItem(item, language, index);
      return _.get(score, 'valid', 1) === 1;
    });
  }

  /*
    * Checks every score in the list of the language
   */
  isScoreListPopulated(item: SegmentFieldItem, language: string) {
    return _.every(this.getListItems(item, language), (value: string, index: number) => {
      let score = this.getScoreIndexItem(item, language, index);
      if (!score) { score = this.getScoreItem(item, language); }
      return _.get(score, 'populated', 1) === 1;
    });
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  /*
    * Replaced parent
    * $event: {status: string, item: SegmentFieldItem, index: number, language: string}
   */
  protected getItemFieldScoreFromEvent($event: any) {
    if (_.isNil($event.index)) {
      return this.getScoreItem($event.item, $event.language);
    } else {
      return this.getScoreIndexItem($event.item, $event.language, $event.index);
    }
  }

  /*
    * Replaced parent
    * $event: {item: SegmentFieldItem, index: number, language: string}
   */
  protected handleControlFocus($event: any) {
    if (!_.isNil($event.index)) {
      this.addNewScore($event.item, $event.index, $event.language);
    }
    this.handleNestedControlFocus($event);
  }

  protected findScorecardItemViaData(data: any): ScorecardItemComponent {
    return this.findScorecardItem('fieldMap', data.item.field_map);
  }

  protected collapseChildScoresViaData(scorecardItem: ScorecardItemComponent, data: any) {
    this.collapseExpandedChildScores(scorecardItem.scorecardChildItems, data.language);
  }

  protected getChildScorecardItemViaData(scorecardItem: ScorecardItemComponent, data: any): ScorecardItemComponent {
    return this.getChildScorecardItemByName(scorecardItem, data.language);
  }

  /*
    * Remove score related to the removed control, by index
    * $event: {item: SegmentFieldItem, index: number, language: string, status}
   */
  protected handleControlRemove($event: any) {
    _.unset(this.segmentScores, `${$event.item.item_name}.${$event.language}.${$event.index}`);
    _.pullAt(this.getItemData($event.item, $event.language), [$event.index]);
    this.ref.markForCheck();
  }

  /*
    * Add score related to the control
    * $event: {item: SegmentFieldItem, index: number, language: string}
   */
  protected handleControlAdd($event: any) {
    if (!$event?.item) { return; }
    this.addNewScore($event.item, $event.index, $event.language);
    this.ref.markForCheck();
  }

  protected getLangValidationMessage(item: any, language: string, index?: number): string {
    let score: any;
    if (_.isNil(index)) {
      score = this.getScoreItem(item, language);
    } else {
      score = this.getScoreIndexItem(item, language, index);
    }
    return this.getValidationMessage(item, score);
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private getScoreItem(item: SegmentFieldItem, language: string): any {
    return _.get(this.segmentScores, `${item.item_name}.${language}`);
  }

  private getScoreIndexItem(item: SegmentFieldItem, language: string, index: number): any {
    return _.get(this.segmentScores, `${item.item_name}.${language}.${index}`);
  }

  /*
    * Add new score after user add new control to the one of the descriptions lists
   */
  private addNewScore(item: SegmentFieldItem, index: number, language: string): void {
    const listData = this.getItemData(item, language);
    // if (this.getScoreIndexItem(item, language, index)) return;
    if (listData && !_.isNil(listData[index])) { return; }
    const scoreItem: any = this.getScoreItem(item, language) || this.getScoreIndexItem(item, language, 0);
    const score: any = _.cloneDeep(scoreItem);
    _.each(['score', 'populated', 'valid', 'possible'], (i: string) => {
      _.set(score, i, 1);
    });
    _.set(score, 'path', `${item.item_name}.${language}.${index}`);
    this.segmentScores[`${item.item_name}.${language}.${index}`] = score;
    if (listData) {
      listData.push('');
    } else {
      _.set(this.itemDetail, `${item.item_name}.${language}`, ['']);
    }
  }

  getDescriptionHint(item: SegmentFieldItem): string {
    if (item.item_name === 'descriptions.ASM' || item.item_name === 'descriptions.KEY') {
      const rule: ValidationItem = item.validation[this.scorecardData?.validation_rule];
      return rule?.hint_detail || item.hint_detail;
    }
    return item.hint_detail;
  }

  getRealType(item: SegmentFieldItem): string {
    if (item.item_name === 'descriptions.ASM') { // It can be both, list and string
      const isList: boolean = _.some(this.languagesList, (language: any) => {
        const firstItem = this.getScoreIndexItem(item, language.code, 0);
        return firstItem && firstItem.populated;
      });
      return isList ? 'list' : 'string';
    }
    return item.type;
  }
}
